import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { updateDoc, doc, deleteField } from 'firebase/firestore';
import { db } from '../types/firebase';
import { Guest, GuestGroup } from '../types/interfaces';
import { FaBus } from 'react-icons/fa';
import { groupNeedsBus } from '../utils/needsBus';

interface RSVPFormProps {
  guestGroup: GuestGroup;
  onRSVPComplete: (updatedGroup: GuestGroup) => void;
  onClose: () => void;
}

function isItalianPlural(inviteName: string): boolean {
  const lower = inviteName.toLowerCase();
  if (lower.includes('famiglia') || lower.includes('family')) return false;
  if (/ e /i.test(inviteName) || / and /i.test(inviteName)) return true;
  return false;
}

function initialNeedsBusChoice(group: GuestGroup): 'yes' | 'no' | undefined {
  const resolved = groupNeedsBus(group);
  if (resolved === true) return 'yes';
  if (resolved === false) return 'no';
  return undefined;
}

const RSVPForm: React.FC<RSVPFormProps> = ({ guestGroup, onRSVPComplete, onClose }) => {
  const { t, i18n } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const [needsBus, setNeedsBus] = useState<'yes' | 'no' | undefined>(() =>
    initialNeedsBusChoice(guestGroup)
  );
  const [allergies, setAllergies] = useState<string>(guestGroup.allergies || '');
  const [formData, setFormData] = useState<Guest[]>(
    guestGroup.guests.map(guest => ({ ...guest }))
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const incompleteGuest = formData.find(guest => guest.coming === undefined);
    if (incompleteGuest) {
      setError(t('complete_form'));
      return;
    }

    const anyGuestComing = formData.some(guest => guest.coming === true);
    if (anyGuestComing && needsBus === undefined) {
      setError(t('complete_form'));
      return;
    }

    try {
      // Spread all fields (preserves metadata like baby, young, side, etc.)
      const updatedGuests: Guest[] = formData.map(guest => ({ ...guest }));

      const needsBusValue = anyGuestComing ? needsBus === 'yes' : false;

      const allergiesValue = anyGuestComing ? allergies.trim() : '';

      await updateDoc(doc(db, 'guestGroups', guestGroup.id), {
        guests: updatedGuests,
        needsBus: needsBusValue,
        busCount: deleteField(),
        allergies: allergiesValue,
        rsvpCode: guestGroup.rsvpCode,
        updatedAt: new Date().toISOString(),
      });

      const updatedGroup: GuestGroup = {
        ...guestGroup,
        guests: updatedGuests,
        needsBus: needsBusValue,
        busCount: undefined,
        allergies: allergiesValue,
      };

      onRSVPComplete(updatedGroup);
      setError(null);
    } catch (err) {
      setError(t('error_rsvp'));
      console.error('Error:', err);
    }
  };

  const updateGuestStatus = (index: number, coming: boolean) => {
    setFormData(prev => prev.map((guest, i) => 
      i === index ? { ...guest, coming } : guest
    ));
  };

  // Check if at least one guest is coming
  const hasGuestsComing = formData.some(guest => guest.coming === true);

  return (
    <div className="flex flex-col">
      <div className="text-center mb-6">
        <h1 className="font-playfair text-2xl font-medium text-[#2d5a2d] mb-2">
          {i18n.language === 'it'
            ? (isItalianPlural(guestGroup.groupInvite) ? 'Gentili' : 'Gentile')
            : t('dear')} {guestGroup.groupInvite},
        </h1>
        <p className="font-body text-sm font-medium text-gray-600">
          {i18n.language === 'it'
            ? (isItalianPlural(guestGroup.groupInvite)
              ? 'Vi preghiamo di confermare la vostra presenza al nostro matrimonio'
              : 'Ti preghiamo di confermare la tua presenza al nostro matrimonio')
            : t('confirm_presence')}
        </p>
      </div>
      
      {error && (
        <div className="bg-[#f5f0e6] border border-[#c9a96e] text-[#8b6914] px-3 py-2 rounded-md mb-4 text-sm font-body">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-3 flex-1 overflow-y-auto">
        {formData.map((guest, index) => (
          <div key={index} className="bg-[#faf8f5] px-3 py-2.5 border border-[#e8e4dc] rounded-md">
            <div className="flex items-center justify-between gap-2">
              <span className="font-body text-base font-medium text-[#2d5a2d]">{guest.fullName === '1' ? '+1' : guest.fullName}</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => updateGuestStatus(index, true)}
                  className={`px-4 py-1.5 text-sm font-body font-medium rounded-md border transition-all duration-200 ${
                    guest.coming === true
                      ? 'bg-[#2d5a2d] text-white border-[#2d5a2d]'
                      : 'bg-white text-[#2d5a2d] border-[#2d5a2d] hover:bg-[#eef1e6]'
                  }`}
                >
                  {t('yes')}
                </button>
                <button
                  type="button"
                  onClick={() => updateGuestStatus(index, false)}
                  className={`px-4 py-1.5 text-sm font-body font-medium rounded-md border transition-all duration-200 ${
                    guest.coming === false
                      ? 'bg-[#9c7b6b] text-white border-[#9c7b6b]'
                      : 'bg-white text-[#9c7b6b] border-[#9c7b6b] hover:bg-[#faf5f3]'
                  }`}
                >
                  {t('no')}
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Shuttle question - only show if at least one guest is coming */}
        {hasGuestsComing && (
          <>
            <div className="bg-[#faf8f5] px-3 py-2.5 border border-[#e8e4dc] rounded-md">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FaBus className="text-[#2d5a2d] w-4 h-4" />
                  <span className="font-body text-sm font-medium text-[#2d5a2d]">{t('need_shuttle')}</span>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setNeedsBus('yes')}
                    className={`px-4 py-1.5 text-sm font-body font-medium rounded-md border transition-all duration-200 ${
                      needsBus === 'yes'
                        ? 'bg-[#2d5a2d] text-white border-[#2d5a2d]'
                        : 'bg-white text-[#2d5a2d] border-[#2d5a2d] hover:bg-[#eef1e6]'
                    }`}
                  >
                    {t('yes')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setNeedsBus('no')}
                    className={`px-4 py-1.5 text-sm font-body font-medium rounded-md border transition-all duration-200 ${
                      needsBus === 'no'
                        ? 'bg-[#9c7b6b] text-white border-[#9c7b6b]'
                        : 'bg-white text-[#9c7b6b] border-[#9c7b6b] hover:bg-[#faf5f3]'
                    }`}
                  >
                    {t('no')}
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-[#faf8f5] px-3 py-2.5 border border-[#e8e4dc] rounded-md">
              <label className="font-body text-sm font-medium text-[#2d5a2d] block mb-1.5">
                {i18n.language === 'it' ? 'Allergie' : 'Allergies'}
              </label>
              <textarea
                value={allergies}
                onChange={(e) => setAllergies(e.target.value)}
                placeholder={i18n.language === 'it' ? 'Segnala eventuali allergie o intolleranze...' : 'Let us know about any allergies or dietary requirements...'}
                className="w-full px-3 py-2 text-sm font-body rounded-md border border-[#e8e4dc] bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#2d5a2d] resize-none"
                rows={2}
              />
            </div>
          </>
        )}

        <div className="flex gap-3 pt-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-white text-[#2d5a2d] border border-[#2d5a2d] font-body font-medium py-2.5 px-4 rounded-md hover:bg-[#eef1e6] transition-colors duration-200"
          >
            {t('cancel')}
          </button>
          <button
            type="submit"
            className="flex-1 bg-[#2d5a2d] text-white border border-[#2d5a2d] font-body font-medium py-2.5 px-4 rounded-md hover:bg-[#245024] transition-colors duration-200"
          >
            {t('submit')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RSVPForm;
